import { DragEndEvent, DragStartEvent, useDndMonitor } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { equals, update } from 'ramda';
import React, { useState, ReactNode, useCallback, useEffect, useMemo } from 'react';

import { Category, ItemBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';
import { MenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext';
import { getMenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext/utils';
import { MenuEditorElement } from 'modules/menu/types';
import { isMenuEditorItem } from 'modules/menu/utils';

type CategoryItemsDnDMonitorProps = {
  onBulkUpdateItemBindingsOrders: (order: string[]) => Promise<void>;
  onUpdateItemBindingsOrder: (order: string[]) => Promise<void>;
  category: Category;
  menuEditorList: MenuEditorElement[];
  children: (data: { draggingId?: string; displayMenuEditorList: MenuEditorElement[] }) => ReactNode;
  onMoveItemsToCategory: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
  onMoveItemToNoCategory: (activeId: string) => Promise<void>;
};

const CategoryItemsDnDMonitor = ({
  menuEditorList,
  children,
  category,
  onBulkUpdateItemBindingsOrders,
  onUpdateItemBindingsOrder,
  onMoveItemToNoCategory,
  onMoveItemsToCategory,
}: CategoryItemsDnDMonitorProps) => {
  // Use cached active due to the issue with virtualized lists cause the active.current to get lost: https://github.com/clauderic/dnd-kit/issues/794
  const [activeEvent, setActiveEvent] = useState<null | MenuEditorDragEventData>(null);
  const [localMenuEditorList, setLocalMenuEditorList] = useState<null | MenuEditorElement[]>(menuEditorList);
  const allItemBindings = useMemo(
    () => [...category.item_bindings, ...category.child_categories.flatMap(category => category.item_bindings)],
    [category.child_categories, category.item_bindings],
  );
  const hasSubcategory = category.child_categories.length > 0;

  const reset = useCallback(() => {
    setLocalMenuEditorList(null);
    setActiveEvent(null);
  }, []);

  useEffect(() => {
    setLocalMenuEditorList(menuEditorList);
    reset();
  }, [menuEditorList, reset]);

  const buildUpdatedItemBindings = useCallback(
    (selectedItemIds: Set<string>, activeItemId: string, overItemId: string) => {
      const selectedItemBindings = allItemBindings.filter(item => selectedItemIds.has(item.item_id));
      const visibleItemBindings = allItemBindings.filter(
        categoryItem => !selectedItemIds.has(categoryItem.item_id) || categoryItem.item_id === activeItemId,
      );

      const newIndex = visibleItemBindings.findIndex(categoryItem => categoryItem.item_id === overItemId);
      const oldIndex = visibleItemBindings.findIndex(categoryItem => categoryItem.item_id === activeItemId);

      const updatedItemBindings = update<ItemBinding | ItemBinding[]>(
        newIndex,
        selectedItemBindings,
        arrayMove(visibleItemBindings, oldIndex, newIndex),
      ).flat();

      return updatedItemBindings;
    },
    [allItemBindings],
  );

  const buildLocalReorderedItemsMenuList = useCallback(
    (itemBindings: ItemBinding[]) =>
      itemBindings
        .map(item =>
          menuEditorList.filter(isMenuEditorItem).find(menuEditorItem => menuEditorItem.itemState.id === item.item_id),
        )
        .filter(isDefined),
    [menuEditorList],
  );

  const buildLocalMenuList = useCallback(
    (itemBindings: ItemBinding[]) =>
      menuEditorList.filter(menuEditorItem =>
        menuEditorItem.type === 'item' ? itemBindings.some(item => item.item_id === menuEditorItem.itemState.id) : true,
      ),
    [menuEditorList],
  );

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const activeEvent = active && getMenuEditorDragEventData(active);
      if (!activeEvent) {
        return;
      }

      setActiveEvent(activeEvent);

      if (hasSubcategory && activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS') {
        return;
      }

      if (activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS') {
        const hiddenItemIds = Array.from(activeEvent.selectedIds?.values() ?? []).filter(id => id !== active.id);
        const updatedDisplayMenuList = menuEditorList.filter(
          menuEditorItem =>
            isMenuEditorItem(menuEditorItem) && !hiddenItemIds.some(id => id === menuEditorItem.itemState.id),
        );

        return setLocalMenuEditorList(updatedDisplayMenuList);
      }
    },
    [hasSubcategory, menuEditorList],
  );

  const onDragEnd = useCallback(
    async ({ over }: DragEndEvent) => {
      try {
        const overEvent = over && getMenuEditorDragEventData(over);
        if (!activeEvent || !overEvent) {
          return;
        }

        if (
          activeEvent.type === 'DND_EDITOR_SINGLE_ITEM' &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY'].includes(overEvent.type) &&
          category.id !== over.id
        ) {
          const localItemBindings = allItemBindings.filter(categoryItem => activeEvent.id !== categoryItem.item_id);
          setLocalMenuEditorList(buildLocalMenuList(localItemBindings));

          return await onMoveItemsToCategory(activeEvent.id, overEvent.id);
        }

        if (
          activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS' &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY'].includes(overEvent.type) &&
          activeEvent.selectedIds &&
          category.id !== overEvent.id
        ) {
          const { selectedIds } = activeEvent;
          const localItemBindings = allItemBindings.filter(categoryItem => !selectedIds.has(categoryItem.item_id));
          setLocalMenuEditorList(buildLocalMenuList(localItemBindings));

          return await onMoveItemsToCategory(activeEvent.id, overEvent.id, activeEvent.selectedIds);
        }

        if (
          activeEvent.type === 'DND_EDITOR_SINGLE_ITEM' &&
          (overEvent.type === 'DND_EDITOR_SINGLE_ITEM' || overEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS')
        ) {
          const localItemBindings = buildUpdatedItemBindings(new Set([activeEvent.id]), activeEvent.id, overEvent.id);

          setLocalMenuEditorList(buildLocalReorderedItemsMenuList(localItemBindings));

          if (equals(localItemBindings, allItemBindings)) {
            return reset();
          }

          const reorderedItemBindingIds = localItemBindings.map(itemBinding => itemBinding.id);
          return await onUpdateItemBindingsOrder(reorderedItemBindingIds);
        }

        if (
          activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS' &&
          activeEvent.selectedIds &&
          (overEvent.type === 'DND_EDITOR_SINGLE_ITEM' || overEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS')
        ) {
          const localItemBindings = buildUpdatedItemBindings(activeEvent.selectedIds, activeEvent.id, overEvent.id);
          setLocalMenuEditorList(buildLocalReorderedItemsMenuList(localItemBindings));
          if (equals(localItemBindings, allItemBindings)) {
            return reset();
          }

          const reorderedItemBindingIds = localItemBindings.map(itemBinding => itemBinding.id);
          return await onBulkUpdateItemBindingsOrders(reorderedItemBindingIds);
        }

        if (activeEvent.type === 'DND_EDITOR_SINGLE_ITEM' && overEvent.type === 'DND_NAVIGATION_NO_CATEGORY') {
          const localItemBindings = allItemBindings.filter(categoryItem => activeEvent.id !== categoryItem.item_id);
          setLocalMenuEditorList(buildLocalMenuList(localItemBindings));

          return await onMoveItemToNoCategory(activeEvent.id);
        }
      } finally {
        reset();
      }
    },
    [
      activeEvent,
      category.id,
      allItemBindings,
      buildLocalMenuList,
      onMoveItemsToCategory,
      buildUpdatedItemBindings,
      buildLocalReorderedItemsMenuList,
      onUpdateItemBindingsOrder,
      reset,
      onBulkUpdateItemBindingsOrders,
      onMoveItemToNoCategory,
    ],
  );

  useDndMonitor({
    onDragStart,
    onDragEnd,
  });

  return (
    <>
      {children({
        draggingId: activeEvent?.id,
        displayMenuEditorList: localMenuEditorList ?? menuEditorList ?? [],
      })}
    </>
  );
};

export default React.memo(CategoryItemsDnDMonitor);
